import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BLOG_POSTS } from './BlogData';

const POSTS_PER_PAGE = 6;

function getCategoryFromLink(relatedLink) {
    if (!relatedLink) return null;
    const map = {
        '/prompts/anxiety': 'Anxiety Relief',
        '/prompts/motivation': 'Motivation',
        '/prompts/mental-health': 'Mental Health',
        '/prompts/overthinking': 'Overthinking',
        '/prompts/self-discovery': 'Self-Discovery',
        '/prompts/gratitude': 'Gratitude',
        '/prompts/procrastination': 'Procrastination',
        '/prompts/burnout': 'Burnout',
        '/personalized-prompt': 'Personalized',
        '/shadow-work': 'Shadow Work',
        '/self-love': 'Self-Love',
    };
    return map[relatedLink] || null;
}

function Journal() {
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.ceil(BLOG_POSTS.length / POSTS_PER_PAGE);
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    const currentPosts = BLOG_POSTS.slice(startIndex, startIndex + POSTS_PER_PAGE);

    const goToPage = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="fade-in">
            <Helmet>
                <title>Insightful Articles on Journaling & Mental Clarity | Mindful Journal Prompts</title>
                <meta name="description" content="Explore expert articles on the science of journaling, shadow work, and mental clarity. Learn how to use writing to improve your life." />
                <link rel="canonical" href="https://www.mindfuljournalprompts.com/journal" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Insightful Articles on Journaling & Mental Clarity | Mindful Journal Prompts" />
                <meta property="og:description" content="Explore expert articles on the science of journaling, shadow work, and mental clarity. Learn how to use writing to improve your life." />
                <meta property="og:url" content="https://www.mindfuljournalprompts.com/journal" />
                <meta property="og:site_name" content="Mindful Journal Prompts" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content="Insightful Articles on Journaling & Mental Clarity | Mindful Journal Prompts" />
                <meta name="twitter:description" content="Explore expert articles on the science of journaling, shadow work, and mental clarity." />
            </Helmet>

            <div style={{ maxWidth: '1400px', width: '100%', margin: '0 auto', padding: '0 2rem', boxSizing: 'border-box' }}>
                <h1 className="prompt-text" style={{ fontSize: '2rem', marginBottom: '1rem' }}>The Sanctuary Logs</h1>
                <p className="meta" style={{ display: 'block', marginBottom: '3rem' }}>NOTES ON CONSCIOUSNESS</p>

                <div className="journal-grid">
                    {currentPosts.map(post => {
                        const category = getCategoryFromLink(post.relatedLink);
                        return (
                            <Link to={`/journal/${post.slug}`} key={post.id} style={{ textDecoration: 'none' }}>
                                <div className="option-card" style={{
                                    textAlign: 'left',
                                    padding: '2rem',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxSizing: 'border-box',
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.75rem',
                                        flexWrap: 'wrap',
                                        gap: '0.5rem',
                                    }}>
                                        <span className="meta" style={{ fontSize: '0.7rem' }}>{post.date}</span>
                                        {category && (
                                            <span style={{
                                                fontSize: '0.65rem',
                                                fontFamily: 'var(--mono)',
                                                letterSpacing: '0.05em',
                                                textTransform: 'uppercase',
                                                color: 'var(--accent)',
                                                background: 'rgba(255,255,255,0.08)',
                                                padding: '0.2rem 0.6rem',
                                                borderRadius: '999px',
                                                border: '1px solid rgba(255,255,255,0.12)',
                                            }}>{category}</span>
                                        )}
                                    </div>
                                    <h2 style={{
                                        fontFamily: 'var(--sans)',
                                        fontSize: '1.25rem',
                                        fontWeight: 500,
                                        color: 'var(--text-main)',
                                        marginBottom: '0.75rem',
                                        lineHeight: 1.35,
                                    }}>
                                        {post.title}
                                    </h2>
                                    <p style={{
                                        fontFamily: 'var(--sans)',
                                        color: 'var(--text-secondary)',
                                        lineHeight: 1.6,
                                        fontSize: '0.9rem',
                                        flex: 1,
                                    }}>
                                        {post.excerpt}
                                    </p>
                                    <span className="text-btn" style={{
                                        marginTop: '1.25rem',
                                        display: 'inline-block',
                                        fontSize: '0.85rem',
                                    }}>Read Entry →</span>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        marginTop: '3rem',
                        marginBottom: '2rem',
                        flexWrap: 'wrap',
                    }}>
                        <button
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: '8px',
                                color: currentPage === 1 ? 'rgba(255,255,255,0.2)' : 'var(--text-main)',
                                padding: '0.5rem 1rem',
                                cursor: currentPage === 1 ? 'default' : 'pointer',
                                fontFamily: 'var(--mono)',
                                fontSize: '0.85rem',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s ease',
                            }}
                        >
                            ← Prev
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                onClick={() => goToPage(page)}
                                style={{
                                    background: page === currentPage
                                        ? 'rgba(255,255,255,0.15)'
                                        : 'rgba(255,255,255,0.04)',
                                    border: page === currentPage
                                        ? '1px solid rgba(255,255,255,0.25)'
                                        : '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: '8px',
                                    color: page === currentPage ? 'var(--text-main)' : 'var(--text-secondary)',
                                    padding: '0.5rem 0.85rem',
                                    cursor: 'pointer',
                                    fontFamily: 'var(--mono)',
                                    fontSize: '0.85rem',
                                    fontWeight: page === currentPage ? 600 : 400,
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.2s ease',
                                    minWidth: '2.5rem',
                                }}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: '8px',
                                color: currentPage === totalPages ? 'rgba(255,255,255,0.2)' : 'var(--text-main)',
                                padding: '0.5rem 1rem',
                                cursor: currentPage === totalPages ? 'default' : 'pointer',
                                fontFamily: 'var(--mono)',
                                fontSize: '0.85rem',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s ease',
                            }}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                .journal-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 2rem;
                }
                @media (max-width: 700px) {
                    .journal-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
}

export default Journal;
